import { useNavigate } from 'react-router-dom'
import { useUserListQueryParams } from 'app/components/users-index/useUserListQueryParams'
import { useEffect, useMemo } from 'react'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useInitialTableSort } from 'app/hooks/useInitialTableSort'
import { useIsOrgUser } from 'app/hooks/useIsOrgUser'
import type { UserJson } from 'app/types/entities'
import { defaultColumns, filterableColumns } from 'app/table-columns/users'
import { useUser } from 'app/utils'
import { useUserStore } from 'app/stores/StoreContext'

export type UseActiveUsersReturn = {
  users: { hits: UserJson[]; total: number } | undefined
  me: UserJson
  columns: ReturnType<typeof defaultColumns>
  filters: ReturnType<typeof filterableColumns>
  initialSort: ReturnType<typeof useInitialTableSort>
  canManageUsers: boolean
  isOrgUser: boolean
}

export function useActiveUsers(): UseActiveUsersReturn {
  const { sort, page, query } = useUserListQueryParams()

  const userStore = useUserStore()
  const me = useUser()
  const isOrgUser = useIsOrgUser()
  const checkPermission = useCheckPermission()
  const canManageUsers = checkPermission([`org:settings:manage-users`, `users:manage`])
  const initialSort = useInitialTableSort(sort)
  const navigate = useNavigate()

  useEffect(() => {
    const [field, direction] = sort?.split(`:`) ?? []
    userStore.fetchList({
      search: query || undefined,
      pagination: { page, limit: 500 },
      sort: field ? { field, direction: (direction?.toUpperCase() as 'ASC' | 'DESC') ?? 'ASC' } : undefined,
    })
  }, [sort, page, query])

  const columns = useMemo(
    () => defaultColumns({ me: me!, canManageUsers, isOrgUser, navigate }),
    [me, canManageUsers, navigate, isOrgUser],
  )

  const filters = useMemo(() => filterableColumns({ showLastLoginDate: true }), [])

  const allUsers = userStore.list as { hits: UserJson[]; total: number } | undefined
  const currentWorkspaceId = me?.workspaceId

  const users = useMemo(() => {
    if (!allUsers || !currentWorkspaceId) return allUsers
    const filtered = allUsers.hits.filter(
      (u) => u.assignedWorkspaceIds?.includes(currentWorkspaceId) || u.workspaceId === currentWorkspaceId,
    )
    return { hits: filtered, total: filtered.length }
  }, [allUsers, currentWorkspaceId])

  return { users, me: me!, columns, filters, initialSort, canManageUsers, isOrgUser }
}
